import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { APP_NAME } from "../constants";
import { getCurrentUser, User } from "../services/authService";
import { adminService, Business } from "../services/adminService";
import TopBar from "../components/TopBar";
import Button from "../components/Button";
import ConfirmationModal from "../components/ConfirmationModal";

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding-top: 20px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  color: #333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${(props) => (props.active ? "#000" : "#ddd")};
  background: ${(props) => (props.active ? "#000" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#333")};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #000;
  }
`;

const BusinessTable = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 100px 150px;
  padding: 16px 20px;
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 80px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 100px 150px;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 80px;
  }
`;

const BusinessInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BusinessName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const BusinessAddress = styled.div`
  font-size: 14px;
  color: #666;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  ${(props) => {
    switch (props.status) {
      case "approved":
      case "active":
        return "background: #e8f5e8; color: #2e7d32;";
      case "rejected":
        return "background: #ffebee; color: #d32f2f;";
      case "pending":
        return "background: #fff3e0; color: #f57c00;";
      default:
        return "background: #f5f5f5; color: #666;";
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{
  variant?: "approve" | "reject" | "delete";
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;

  ${(props) => {
    switch (props.variant) {
      case "approve":
        return "background: #4caf50; color: white; &:hover { background: #45a049; }";
      case "reject":
        return "background: #ff9800; color: white; &:hover { background: #f57c00; }";
      case "delete":
        return "background: #f44336; color: white; &:hover { background: #d32f2f; }";
      default:
        return "background: #2196f3; color: white; &:hover { background: #1976d2; }";
    }
  }}

  span {
    display: none;
  }

  @media (max-width: 768px) {
    width: auto;
    height: auto;
    padding: 8px 12px;
    white-space: nowrap;

    i {
      display: none;
    }

    span {
      display: inline;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 20px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
`;

const LoadingPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 18px;
  color: #888;
`;

const MobileBusinessCard = styled.div`
  display: none;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: block;
  }
`;

const AdminBusinessesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>(
    searchParams.get("filter") || "all"
  );
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    business: Business | null;
  }>({
    show: false,
    business: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);

        if (!userData || !adminService.isAdmin(userData)) {
          navigate("/dashboard");
          return;
        }

        const businessesData = await adminService.getAllBusinesses();
        setBusinesses(businessesData);
      } catch (error) {
        console.error("Error fetching data:", error);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);
  useEffect(() => {
    let filtered = businesses;

    if (filter !== "all") {
      if (filter === "approved") {
        // Filter for both 'approved' and 'active' statuses
        filtered = businesses.filter(
          (business) =>
            business.status === "approved" || business.status === "active"
        );
      } else {
        filtered = businesses.filter((business) => business.status === filter);
      }
    }

    setFilteredBusinesses(filtered);
  }, [businesses, filter]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setSearchParams({ filter: newFilter });
  };
  const handleStatusUpdate = async (
    businessId: string,
    status: "approved" | "rejected" | "pending"
  ) => {
    try {
      // When approving, set status to 'active' instead of 'approved'
      const actualStatus = status === "approved" ? "active" : status;
      await adminService.updateBusinessStatus(businessId, actualStatus as any);
      setBusinesses((prev) =>
        prev.map((business) =>
          business.id === businessId
            ? { ...business, status: actualStatus as any }
            : business
        )
      );
    } catch (error) {
      console.error("Error updating business status:", error);
    }
  };
  const handleDeleteBusiness = async () => {
    if (!deleteModal.business) return;

    try {
      await adminService.deleteBusiness(deleteModal.business.id);
      setBusinesses((prev) =>
        prev.filter((b) => b.id !== deleteModal.business!.id)
      );
      setDeleteModal({ show: false, business: null });
    } catch (error) {
      console.error("Error deleting business:", error);
    }
  };
  const getStatusCount = (status: string) => {
    if (status === "all") return businesses.length;
    if (status === "approved") {
      // Count both 'approved' and 'active' statuses
      return businesses.filter(
        (b) => b.status === "approved" || b.status === "active"
      ).length;
    }
    return businesses.filter((b) => b.status === status).length;
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <LoadingPlaceholder>Loading businesses...</LoadingPlaceholder>
        </ContentWrapper>
      </Container>
    );
  }
  if (!user || !adminService.isAdmin(user)) {
    return null;
  }
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Admin Businesses</title>
        <meta name="description" content={`${APP_NAME} - Admin Businesses`} />
      </Helmet>
      <Container>
        <TopBar user={user} />
        <ContentWrapper>
          <HeaderSection>
            <PageTitle>Business Management</PageTitle>
          </HeaderSection>

          <Breadcrumbs>
            <BreadcrumbLink onClick={() => navigate("/admin")}>
              Admin Dashboard
            </BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Business Management</span>
          </Breadcrumbs>

          <FilterSection>
            <FilterButton
              active={filter === "all"}
              onClick={() => handleFilterChange("all")}
            >
              All ({getStatusCount("all")})
            </FilterButton>
            <FilterButton
              active={filter === "pending"}
              onClick={() => handleFilterChange("pending")}
            >
              Pending ({getStatusCount("pending")})
            </FilterButton>
            <FilterButton
              active={filter === "approved"}
              onClick={() => handleFilterChange("approved")}
            >
              Approved ({getStatusCount("approved")})
            </FilterButton>
            <FilterButton
              active={filter === "rejected"}
              onClick={() => handleFilterChange("rejected")}
            >
              Rejected ({getStatusCount("rejected")})
            </FilterButton>
          </FilterSection>

          {filteredBusinesses.length > 0 ? (
            <BusinessTable>
              {" "}
              <TableHeader>
                <div>Business</div>
                {/* <div style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>Manager ID</div> */}
                {/* <div style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>Email</div> */}
                <div
                  style={{
                    display: window.innerWidth <= 768 ? "none" : "block",
                  }}
                >
                  Address
                </div>
                <div
                  style={{
                    display: window.innerWidth <= 768 ? "none" : "block",
                  }}
                >
                  Status
                </div>
                <div>Actions</div>
              </TableHeader>
              {filteredBusinesses.map((business) => (
                <TableRow key={business.id}>
                  <BusinessInfo>
                    <BusinessName>{business.name}</BusinessName>
                    {/* <BusinessAddress>{business.address}</BusinessAddress> */}
                  </BusinessInfo>
                  {/* <div style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>
                  {business.managerId}
                </div>
                  <div style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>
                  {business.email && <div style={{ fontSize: '12px', color: '#666' }}>{business.email}</div>}
                </div> */}
                <div style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>
                  {business.email && <div style={{ fontSize: '12px', color: '#666' }}>{business.address}</div>}
                </div>
                  <div
                    style={{
                      display: window.innerWidth <= 768 ? "none" : "block",
                    }}
                  >
                    <StatusBadge status={business.status}>
                      {business.status}
                    </StatusBadge>
                  </div>{" "}
                  <ActionButtons>
                    {business.status !== "approved" &&
                      business.status !== "active" && (
                        <ActionButton
                          variant="approve"
                          onClick={() =>
                            handleStatusUpdate(business.id, "approved")
                          }
                          title="Approve Business"
                        >
                          <i
                            className="bx bx-check"
                            style={{ fontSize: "16px" }}
                          ></i>
                          <span style={{ display: "none" }}>Approve</span>
                        </ActionButton>
                      )}
                    {business.status !== "rejected" && (
                      <ActionButton
                        variant="reject"
                        onClick={() =>
                          handleStatusUpdate(business.id, "rejected")
                        }
                        title="Reject Business"
                      >
                        <i className="bx bx-x" style={{ fontSize: "16px" }}></i>
                        <span style={{ display: "none" }}>Reject</span>
                      </ActionButton>
                    )}
                    <ActionButton
                      variant="delete"
                      onClick={() => setDeleteModal({ show: true, business })}
                      title="Delete Business"
                    >
                      <i
                        className="bx bx-trash"
                        style={{ fontSize: "16px" }}
                      ></i>
                      <span style={{ display: "none" }}>Delete</span>
                    </ActionButton>
                  </ActionButtons>
                </TableRow>
              ))}
            </BusinessTable>
          ) : (
            <EmptyState>
              <EmptyTitle>No businesses found</EmptyTitle>
              <EmptyText>
                {filter === "all"
                  ? "No businesses have been registered yet."
                  : `No businesses with ${filter} status found.`}
              </EmptyText>
            </EmptyState>
          )}

          <ConfirmationModal
            isOpen={deleteModal.show}
            title="Delete Business"
            message={`Are you sure you want to delete "${deleteModal.business?.name}"? This action cannot be undone.`}
            onConfirm={handleDeleteBusiness}
            onCancel={() => setDeleteModal({ show: false, business: null })}
            confirmText="Delete"
            cancelText="Cancel"
          />
        </ContentWrapper>
      </Container>
    </>
  );
};

export default AdminBusinessesPage;
